// ══════════════════════════════════════════════════════════════════
//  ED CRM - Hook de Notifications (TanStack Query)
// ══════════════════════════════════════════════════════════════════
//  Caixa de entrada do sino: lista + contador de não-lidas, assinatura
//  realtime da tabela `notifications` filtrada pelo próprio usuário, e
//  o "empurrão" para a notificação nativa do navegador.
//
//  Mesmo desenho do useActivities: o realtime só invalida a query
//  (debounced); cache/dedup/revalidação são do TanStack Query.
// ══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationEntry } from '@/types/notification';
import {
  subscribeToTable,
  listNotifications,
  countUnreadNotifications,
  markNotificationsRead,
  markAllNotificationsRead,
  dismissNotification,
} from '@app';
import { useAuth } from '@hooks/useAuth';
import { qk } from '@queries';
import { showBrowserNotification } from '@components/Notifications/browserNotify';

const STALE = 30 * 1000;

export interface UseNotificationsOptions {
  /** Dispara a notificação nativa do navegador para cada item novo. */
  browserPush?: boolean;
  /** Clique na notificação nativa. Recebe a notificação que chegou. */
  onNotificationClick?: (notification: NotificationEntry) => void;
  /** Exibe o alerta dentro do CRM quando um item novo chega em tempo real. */
  onNewNotification?: (notification: NotificationEntry) => void;
}

export const useNotifications = ({
  browserPush = true,
  onNotificationClick,
  onNewNotification,
}: UseNotificationsOptions = {}) => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id || null;

  const listQuery = useQuery<NotificationEntry[]>({
    queryKey: qk.notifications(userId || ''),
    queryFn: () => listNotifications({ userId: userId || '' }),
    enabled: !!userId && !authLoading,
    staleTime: STALE,
  });

  const unreadQuery = useQuery<number>({
    queryKey: qk.notificationsUnread(userId || ''),
    queryFn: () => countUnreadNotifications(userId),
    enabled: !!userId && !authLoading,
    staleTime: STALE,
  });

  const items = useMemo<NotificationEntry[]>(() => listQuery.data ?? [], [listQuery.data]);
  const unreadCount = unreadQuery.data ?? 0;

  const reload = useCallback(() => {
    if (!userId) return;
    // Prefixo comum às duas queries — invalida lista e contador de uma vez.
    queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
  }, [queryClient, userId]);

  // ─── Realtime → invalidate (debounced) ────────────────────────
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!userId) return undefined;
    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(reload, 250);
    };
    const unsub = subscribeToTable({
      table: 'notifications',
      filter: `user_id=eq.${userId}`,
      onChange: schedule,
    });
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      unsub();
    };
  }, [userId, reload]);

  // ─── Empurra para o navegador o que chegou depois da montagem ──
  // `seenRef` começa preenchido com o primeiro lote: notificação antiga
  // não estoura popup ao abrir o CRM, só o que chega de fato.
  const seenRef = useRef<Set<string> | null>(null);
  const clickRef = useRef(onNotificationClick);
  const newNotificationRef = useRef(onNewNotification);
  clickRef.current = onNotificationClick;
  newNotificationRef.current = onNewNotification;

  useEffect(() => {
    if (!browserPush) return;
    if (seenRef.current === null) {
      seenRef.current = new Set(items.map(n => n.id));
      return;
    }
    const seen = seenRef.current;
    for (const n of items) {
      if (seen.has(n.id)) continue;
      seen.add(n.id);
      if (n.read_at) continue;
      newNotificationRef.current?.(n);
      showBrowserNotification({
        title: n.title,
        body: n.body,
        tag: n.id,
        onClick: () => clickRef.current?.(n),
      });
    }
  }, [items, browserPush]);

  // ─── Mutations (otimistas onde compensa) ──────────────────────
  const markRead = useCallback(async (ids: string[]) => {
    const pending = ids.filter(Boolean);
    if (pending.length === 0) return;
    const now = new Date().toISOString();
    queryClient.setQueryData<NotificationEntry[]>(
      qk.notifications(userId || ''),
      (rows: NotificationEntry[] | undefined) => (rows || []).map(
        (r: NotificationEntry) => (pending.includes(r.id) && !r.read_at ? { ...r, read_at: now } : r),
      ),
    );
    try {
      await markNotificationsRead(pending);
    } finally {
      reload();
    }
  }, [queryClient, reload, userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    try {
      await markAllNotificationsRead(userId);
    } finally {
      reload();
    }
  }, [reload, userId]);

  const dismiss = useCallback(async (id: string) => {
    try {
      await dismissNotification(id);
    } finally {
      reload();
    }
  }, [reload]);

  return {
    notifications: items,
    unreadCount,
    loading: listQuery.isLoading,
    error: listQuery.error,
    refetch: reload,
    markRead,
    markAllRead,
    dismiss,
  };
};
